import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ExternalLink } from "lucide-react";

const AV_BY_LINK = "https://av.by/company/transport/3049756";

const navLinks = [
  { href: "#services", label: "Услуги" },
  { href: "#advantages", label: "Преимущества" },
  { href: "#examples", label: "Примеры" },
  { href: "#calculator", label: "Калькулятор" },
  { href: "#contact", label: "Контакты" },
];

export default function Header({ mobileMenuOpen, setMobileMenuOpen }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled 
            ? "glass py-3" 
            : "bg-transparent py-6"
        }`}
        data-testid="header"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a 
              href="#" 
              className="flex items-center gap-3 group"
              data-testid="logo"
            >
              <div className="w-10 h-10 border border-white/30 flex items-center justify-center group-hover:border-white transition-colors duration-300">
                <span className="text-white font-bold text-xs tracking-wider">SBE</span>
              </div>
              <div className="hidden sm:block">
                <span className="text-white text-lg font-light tracking-wide">
                  SBE.by
                </span>
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="text-white/60 hover:text-white text-xs uppercase tracking-widest transition-colors duration-300"
                  data-testid={`nav-${link.href.slice(1)}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <a 
                href={AV_BY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white/60 hover:text-white text-xs uppercase tracking-widest transition-colors duration-300"
                data-testid="header-avby-btn"
              >
                <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                Авто в наличии
              </a>
              <button 
                onClick={() => scrollToSection("#contact")}
                className="btn-premium"
                data-testid="header-cta-btn"
              >
                Заявка
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-white"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" strokeWidth={1} />
              ) : (
                <Menu className="w-6 h-6" strokeWidth={1} />
              )}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8">
              {navLinks.map((link, index) => (
                <motion.button
                  key={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => scrollToSection(link.href)}
                  className="text-white text-2xl font-light tracking-wide"
                  data-testid={`mobile-nav-${link.href.slice(1)}`}
                >
                  {link.label}
                </motion.button>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col gap-4 mt-8"
              >
                <a 
                  href={AV_BY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-premium-outline"
                >
                  Авто в наличии
                </a>
                <button 
                  onClick={() => scrollToSection("#contact")}
                  className="btn-premium"
                >
                  Оставить заявку
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
